// Fragen am Stück (E3, S6; CLAUDE.md, Grundsatz 9: „Fragen stellt man im
// System, an dem Ding, um das es geht“) – EIN Baustein für jede Stelle im
// CRM, an der eine Frage hängen kann: das ganze Projekt (Anlage) und die
// Besichtigung oben in der Freigabe, jede Station, jedes Foto (in der großen
// Ansicht), jede Notiz – und die Akte der Anlage. Kein allgemeiner Chat:
// Frage und Antwort bleiben an ihrem Stück.
//
// Seit dem Augentest am 21.10.2026 ein eigenes Muster, überall gleich
// (Gernot: „dezent, aber nicht zu übersehen – hochwertig“; die Doku-App hat
// dasselbe):
//  - `FrageStellen`: zu eine Pille „Frage stellen“ mit dem Zeichen (Kreis in
//    Kohle, Fragezeichen in Lime). Woran gefragt wird („zu diesem Foto“),
//    sagt der Knopf dem Bildschirmleser; offen ein Feld mit „Frage senden“.
//  - `FrageKarte`: ein Gespräch als Karte – links ein Streifen, Lime solange
//    offen, grau wenn erledigt; oben wer (rundes Zeichen mit Buchstaben),
//    wann und der Stand; darunter der Text (wie getippt), die Antworten
//    leicht eingerückt, und das Antwortfeld mit „Senden“ und „Erledigt“ –
//    steht schon Text im Feld, „Senden und erledigt“ in einem. Erledigt:
//    wer, wann, „Wieder öffnen“ – leise.
//  - `OffeneFragenMarke`: die kleine Lime-Pille „1 offene Frage“ an einem
//    Stück (Station, Notiz) – ein Klick springt hin.
//  - `FragenAmStueck`: die Fragen an einem Stück, darunter „Frage stellen“.
// Keine Erklär-Absätze, wie Fragen gehen – das Muster sagt es selbst.
// In jedem Textfeld der Hinweis, wie man auf diesem Gerät diktiert, und
// darunter leise „Wie diktiere ich?“ (DiktatHilfe, seit 22.10.2026) – ein
// Klick klappt den einen Satz für dieses Gerät auf.
//
// Gesendet wird direkt an den Server (/api/crm/…) – das Büro sitzt im Netz.
// Geht es nicht, steht der Grund an der Stelle, der Text bleibt im Feld.
// Die Kennung entsteht im Browser (Kern, `neueKennung`) und bleibt dieselbe,
// solange derselbe Text noch einmal geht: Ein zweites Senden nach einem
// Funkloch legt nichts doppelt an (fragen.ts, `kennungFuer`). Danach lädt
// die Seite neu und sagt es den anderen (speicher.ts) – so merken es das
// Band, die Zahl in der Seitenleiste und ein zweites Fenster.
//
// Text und Stück einer Frage stehen nach dem Senden fest (server/src/
// fragen.ts) – berichtigt wird im Gespräch darunter.

import { useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import {
  BUERO_GRENZEN,
  neueKennung,
  type AntwortEingabe,
  type BueroAntwort,
  type FrageAmServer,
  type FrageEingabe,
  type FrageStand,
  type FrageStandEingabe,
} from "@happy-energy/kern";
import { ApiFehler, Chip, DiktatHilfe, Ikon, mitDiktat, senden, toastZeigen } from "@happy-energy/oberflaeche";
import { wannZeigen } from "../anzeige.ts";
import { meldungAus } from "../meldung.ts";
import { anderenFensternMelden } from "../speicher.ts";
import { useCrm } from "../zusammenhang.tsx";
import { aeltesteZuerst, gespraechText, kennungFuer, offenText, werText, type Bezug, type Versuch } from "./fragen.ts";
import { frageElementId } from "./sprung.ts";
import { FrageAvatar, FrageZeichen } from "./zeichen.tsx";
import "../formular.css";
import "./fragen.css";

/** Strg + Enter (am Mac ⌘ + Enter) schickt ab – Enter allein macht eine neue Zeile. */
function strgEnter(e: KeyboardEvent<HTMLTextAreaElement>): boolean {
  if (e.key !== "Enter" || !(e.ctrlKey || e.metaKey)) return false;
  e.preventDefault();
  return true;
}

/** Ist es am Server inzwischen anders (gibt es nicht mehr, passt nicht)? Dann den neuen Stand zeigen. */
function amServerAnders(fehler: unknown): boolean {
  return fehler instanceof ApiFehler && (fehler.status === 404 || fehler.status === 409);
}

// --- Eine Frage ------------------------------------------------------------------------------

/**
 * Eine Frage mit ihren Antworten und dem, was man tun kann. `zusammenhang`:
 * wenn sie nicht an ihrem Stück steht (Seite „Fragen“, Akte) – woran sie
 * hängt.
 */
export function FrageKarte({ frage, neuLaden, zusammenhang }: { frage: FrageAmServer; neuLaden: () => void; zusammenhang?: ReactNode }) {
  const { ich } = useCrm();
  const [entwurf, setEntwurf] = useState("");
  const [arbeitet, setArbeitet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  /** Der letzte Versuch – dieselbe Kennung, wenn derselbe Text noch einmal geht. */
  const letzter = useRef<Versuch | null>(null);
  const offen = frage.stand === "offen";
  const text = gespraechText(entwurf);
  const eigene = frage.von.id === ich.nutzer.id;

  function fertig() {
    anderenFensternMelden(frage.aufnahmeId);
    neuLaden();
  }

  async function antworten(undErledigt: boolean) {
    if (text === null) return;
    const versuch = kennungFuer(letzter.current, text, neueKennung);
    letzter.current = versuch;
    setArbeitet(true);
    setFehler(null);
    let geantwortet = false;
    try {
      await senden<BueroAntwort>("/api/crm/antwort", {
        id: versuch.id,
        frageId: frage.id,
        text: versuch.text,
        geantwortetAm: new Date().toISOString(),
      } satisfies AntwortEingabe);
      geantwortet = true;
      letzter.current = null;
      setEntwurf("");
      if (undErledigt) await senden<BueroAntwort>(`/api/crm/frage/${frage.id}/stand`, { stand: "erledigt" } satisfies Pick<FrageStandEingabe, "stand">);
      toastZeigen(undErledigt ? "Beantwortet und erledigt" : "Antwort gesendet");
      fertig();
    } catch (f) {
      setFehler(meldungAus(f));
      // Die Antwort ist schon da (nur „erledigt“ ging nicht) – oder am Server ist es anders: den neuen Stand zeigen.
      if (geantwortet || amServerAnders(f)) fertig();
    } finally {
      setArbeitet(false);
    }
  }

  async function standSetzen(stand: FrageStand) {
    setArbeitet(true);
    setFehler(null);
    try {
      await senden<BueroAntwort>(`/api/crm/frage/${frage.id}/stand`, { stand } satisfies Pick<FrageStandEingabe, "stand">);
      toastZeigen(stand === "erledigt" ? "Als erledigt vermerkt" : "Wieder offen");
      fertig();
    } catch (f) {
      setFehler(meldungAus(f));
      if (amServerAnders(f)) fertig();
    } finally {
      setArbeitet(false);
    }
  }

  const erledigtVon = frage.erledigtVon ? ` von ${frage.erledigtVon.id === ich.nutzer.id ? "dir" : frage.erledigtVon.name}` : "";

  return (
    <article
      id={frageElementId(frage.id)}
      className={`frage-karte ${offen ? "offen" : "erledigt"}`}
      aria-label={eigene ? "Deine Frage" : `Frage von ${frage.von.name}`}
    >
      {zusammenhang && <div className="frage-zusammenhang">{zusammenhang}</div>}
      <div className="frage-kopf">
        <FrageAvatar name={frage.von.name} />
        <span className="frage-wer">
          <b>{werText(frage.von, ich.nutzer.id)}</b>
          <span className="frage-zeit">{wannZeigen(frage.gestelltAm)}</span>
        </span>
        {offen ? <Chip art="lime">offen</Chip> : <Chip ikon="haken">erledigt</Chip>}
      </div>

      <div className="frage-rumpf">
        <p className="frage-text">{frage.text}</p>

        {frage.antworten.length > 0 && (
          <ol className="frage-antworten" aria-label="Antworten">
            {frage.antworten.map((x) => (
              <li key={x.id} className={x.von.id === ich.nutzer.id ? "meine" : undefined}>
                <FrageAvatar name={x.von.name} klein />
                <div className="frage-antwort-wort">
                  <span className="frage-wer">
                    <b>{werText(x.von, ich.nutzer.id)}</b>
                    <span className="frage-zeit">{wannZeigen(x.geantwortetAm)}</span>
                  </span>
                  <p className="frage-text">{x.text}</p>
                </div>
              </li>
            ))}
          </ol>
        )}

        {offen ? (
          <div className="frage-antworten-feld">
            <textarea
              className="frage-feld"
              rows={1}
              maxLength={BUERO_GRENZEN.frageText}
              value={entwurf}
              placeholder={mitDiktat("Antworten")}
              aria-label="Antwort auf diese Frage"
              onChange={(e) => setEntwurf(e.target.value)}
              onKeyDown={(e) => {
                if (strgEnter(e)) void antworten(false);
              }}
            />
            <div className="frage-knoepfe">
              <button type="button" className="crm-knopf dunkel klein" disabled={arbeitet || text === null} onClick={() => void antworten(false)}>
                {arbeitet ? <span className="kreisel" aria-hidden="true" /> : <Ikon name="senden" groesse="k" />} Senden
              </button>
              <button
                type="button"
                className="crm-knopf rand klein"
                disabled={arbeitet}
                onClick={() => void (text === null ? standSetzen("erledigt") : antworten(true))}
              >
                <Ikon name="haken" groesse="k" /> {text === null ? "Erledigt" : "Senden und erledigt"}
              </button>
            </div>
            <DiktatHilfe />
          </div>
        ) : (
          <div className="frage-erledigt">
            <span>
              <Ikon name="haken" groesse="k" /> Erledigt{erledigtVon}
              {frage.erledigtAm ? ` · ${wannZeigen(frage.erledigtAm)}` : ""}
            </span>
            <button type="button" className="crm-knopf leise klein" disabled={arbeitet} onClick={() => void standSetzen("offen")}>
              Wieder öffnen
            </button>
          </div>
        )}
        {fehler && (
          <p className="crm-fehler-satz" role="alert">
            {fehler}
          </p>
        )}
      </div>
    </article>
  );
}

// --- Eine Frage stellen -------------------------------------------------------------------------

/**
 * „Frage stellen“ an einem Stück: zu die Pille, offen ein Feld mit „Frage
 * senden“. `wo` („zu diesem Foto“) sagt der Knopf dem Bildschirmleser und
 * steht über dem offenen Feld – sichtbar ist am Knopf nur „Frage stellen“,
 * überall gleich.
 */
export function FrageStellen({ aufnahmeId, bezug, wo, neuLaden }: { aufnahmeId: string; bezug: Bezug; wo: string; neuLaden: () => void }) {
  const { ich } = useCrm();
  const [offen, setOffen] = useState(false);
  const [entwurf, setEntwurf] = useState("");
  const [arbeitet, setArbeitet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const letzter = useRef<Versuch | null>(null);
  const text = gespraechText(entwurf);
  const feld = `frage-neu-${bezug.tabelle}-${bezug.id}`;

  async function stellen() {
    if (text === null) return;
    const versuch = kennungFuer(letzter.current, text, neueKennung);
    letzter.current = versuch;
    setArbeitet(true);
    setFehler(null);
    try {
      await senden<BueroAntwort>("/api/crm/frage", {
        id: versuch.id,
        aufnahmeId,
        bezug,
        text: versuch.text,
        gestelltAm: new Date().toISOString(),
      } satisfies FrageEingabe);
      letzter.current = null;
      setEntwurf("");
      setOffen(false);
      toastZeigen("Frage gestellt");
      anderenFensternMelden(aufnahmeId);
      neuLaden();
    } catch (f) {
      setFehler(meldungAus(f));
    } finally {
      setArbeitet(false);
    }
  }

  function abbrechen() {
    if (arbeitet) return;
    setOffen(false);
    setEntwurf("");
    setFehler(null);
    letzter.current = null;
  }

  if (!offen) {
    return (
      <button type="button" className="crm-knopf frage-knopf" aria-label={`Frage ${wo} stellen`} onClick={() => setOffen(true)}>
        {/* Seit E3b (O81 a): am Knopf nur das Fragezeichen, ohne Kreis, mittig (Gernot beim Nachtest, 22.10.2026). */}
        <span className="frage-knopf-zeichen" aria-hidden="true">
          <Ikon name="fragezeichen" groesse="k" />
        </span>
        Frage stellen
      </button>
    );
  }

  return (
    <div
      className="frage-stellen"
      role="group"
      aria-label={`Frage ${wo} stellen`}
      onKeyDown={(e) => {
        // Esc schließt nur das Feld – nicht auch die große Foto-Ansicht darum herum.
        if (e.key === "Escape") {
          e.stopPropagation();
          abbrechen();
        }
      }}
    >
      <div className="frage-kopf">
        <FrageAvatar name={ich.nutzer.name} />
        <label className="frage-stellen-titel" htmlFor={feld}>
          Deine Frage {wo}
        </label>
      </div>
      <div className="frage-rumpf">
        <textarea
          id={feld}
          className="frage-feld"
          rows={2}
          // Aufgeklappt, um zu tippen – der Cursor steht gleich im Feld.
          autoFocus
          maxLength={BUERO_GRENZEN.frageText}
          value={entwurf}
          placeholder={mitDiktat("Deine Frage")}
          onChange={(e) => setEntwurf(e.target.value)}
          onKeyDown={(e) => {
            if (strgEnter(e)) void stellen();
          }}
        />
        <DiktatHilfe />
        <div className="frage-knoepfe">
          <span className="frage-tipp">Strg + Enter sendet</span>
          <button type="button" className="crm-knopf dunkel klein" disabled={arbeitet || text === null} onClick={() => void stellen()}>
            {arbeitet ? <span className="kreisel" aria-hidden="true" /> : <Ikon name="senden" groesse="k" />} Frage senden
          </button>
          <button type="button" className="crm-knopf rand klein" disabled={arbeitet} onClick={abbrechen}>
            Abbrechen
          </button>
        </div>
        {fehler && (
          <p className="crm-fehler-satz" role="alert">
            {fehler}
          </p>
        )}
      </div>
    </div>
  );
}

// --- Die Marke an einem Stück ----------------------------------------------------------------------

/**
 * „1 offene Frage“ an einem Stück (Station, Notiz) – die kleine Lime-Pille,
 * ein Klick springt zur ältesten offenen. Nichts offen: nichts zu sehen.
 * `wo`: für den Bildschirmleser („an Station 3“).
 */
export function OffeneFragenMarke({
  fragen,
  zu,
  wo,
}: {
  fragen: readonly Pick<FrageAmServer, "id" | "stand" | "gestelltAm">[];
  zu: (frageId: string) => void;
  wo: string;
}) {
  const offen = aeltesteZuerst(fragen.filter((f) => f.stand === "offen"));
  const erste = offen[0];
  if (!erste) return null;
  const text = offenText(offen.length);
  return (
    <button type="button" className="crm-knopf frage-marke" aria-label={`${text} ${wo} – ansehen`} title="Zur Frage" onClick={() => zu(erste.id)}>
      <FrageZeichen /> {text}
    </button>
  );
}

// --- Die Fragen an einem Stück --------------------------------------------------------------------

/**
 * Die Fragen an einem Stück, darunter „Frage stellen“. Keine Fragen: nur
 * die Pille. `fragen`: schon die an diesem Stück (fragen.ts, `fragenAn`),
 * älteste zuerst.
 */
export function FragenAmStueck({
  aufnahmeId,
  bezug,
  fragen,
  neuLaden,
  wo,
}: {
  aufnahmeId: string;
  bezug: Bezug;
  fragen: readonly FrageAmServer[];
  neuLaden: () => void;
  /** „zu diesem Foto“, „zu Station 3“ */
  wo: string;
}) {
  return (
    <div className={`fragen-am-stueck${fragen.length > 0 ? " mit-fragen" : ""}`}>
      {fragen.map((f) => (
        <FrageKarte key={f.id} frage={f} neuLaden={neuLaden} />
      ))}
      <FrageStellen aufnahmeId={aufnahmeId} bezug={bezug} wo={wo} neuLaden={neuLaden} />
    </div>
  );
}
